import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import CodeEditor from "@uiw/react-textarea-code-editor";
import {
  useCallback,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from "react";
import {
  useCreateTestEdiOrderMutation,
  type TestEdiOrderResult,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import { EXAMPLE_EDI_PAYLOAD_JSON } from "../example-edi-payload";
import OrderOutcomeAlert from "./order-outcome-alert";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } satisfies SxProps<Theme>,
  editor: {
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    maxHeight: 520,
    overflow: "auto",
  } satisfies SxProps<Theme>,
  // CodeEditor styles its own textarea through a plain style prop rather than
  // the MUI system, so this one is CSS rather than an sx value.
  editorTextarea: {
    fontSize: 12,
    backgroundColor: "#f5f5f5",
    fontFamily:
      "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
  } satisfies CSSProperties,
};

/**
 * Parses the editor's contents into the object the mutation's `payload`
 * argument takes.
 *
 * @param raw The editor text.
 * @returns The parsed object, or the reason it could not be used.
 */
const parsePayload = (
  raw: string,
): { payload: Record<string, unknown> } | { problem: string } => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return {
      problem: `The payload is not valid JSON: ${
        error instanceof Error ? error.message : String(error)
      }`,
    };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { problem: "The payload must be a JSON object." };
  }
  return { payload: parsed as Record<string, unknown> };
};

type EdiPayloadPanelProps = {
  /** Disables submission, used when the selected environment is production. */
  readonly disabled: boolean;
};

/**
 * The raw-payload tab: post a Stedi/Boomi-shaped inbound EDI body through the
 * same handler `POST /edi-inbound` calls, so the order lands with
 * `OrderSource.EDI` — which outranks API in duplicate-merge priority.
 *
 * The payload is not modelled as a form because the real endpoint accepts
 * whatever the partner platform sends, and reproducing a partner's transaction
 * means sending that body unchanged.
 */
const EdiPayloadPanel = ({ disabled }: EdiPayloadPanelProps) => {
  const [raw, setRaw] = useState(EXAMPLE_EDI_PAYLOAD_JSON);
  const [outcome, setOutcome] = useState<TestEdiOrderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createTestEdiOrder, { loading: submitting }] =
    useCreateTestEdiOrderMutation();

  const submit = useCallback(async () => {
    setError(null);
    setOutcome(null);
    const parsed = parsePayload(raw);
    if ("problem" in parsed) {
      setError(parsed.problem);
      return;
    }
    try {
      const result = await createTestEdiOrder({
        variables: { input: { payload: parsed.payload } },
      });
      throwOnGraphqlErrors(result);
      const created = result.data?.createTestEdiOrder ?? null;
      if (created === null) {
        setError("The mutation returned no result.");
        return;
      }
      setOutcome(created);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Sending the payload failed.",
      );
    }
  }, [createTestEdiOrder, raw]);

  const handleRawChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setRaw(event.target.value);
    },
    [],
  );

  const handleSubmitClick = useCallback(() => {
    void submit();
  }, [submit]);

  const handleResetClick = useCallback(() => {
    setRaw(EXAMPLE_EDI_PAYLOAD_JSON);
    setOutcome(null);
    setError(null);
  }, []);

  return (
    <Box sx={styles.panel}>
      <Alert severity="info">
        <AlertTitle>This runs the real inbound EDI handler</AlertTitle>
        <Typography variant="body2">
          The target environment must resolve receiverId (with the optional
          SCAC) to a company and senderId (with the optional senderContactCode)
          to one of its contacts. Without a match the handler records a FAILED
          EDI transaction and throws, exactly as it does for a real partner
          transaction. It sends no Slack alert: Slack emission is suppressed
          outside production, and this tool refuses to run on production.
        </Typography>
        <Typography variant="body2">
          Keep usageIndicatorCode at P. A T payload returns early without
          creating anything.
        </Typography>
      </Alert>

      <Box sx={styles.editor}>
        <CodeEditor
          language="json"
          padding={12}
          placeholder="Paste an inbound EDI payload"
          style={styles.editorTextarea}
          value={raw}
          onChange={handleRawChange}
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Button
          disabled={disabled || submitting}
          variant="contained"
          onClick={handleSubmitClick}
        >
          Send EDI payload
        </Button>
        <Button disabled={submitting} onClick={handleResetClick}>
          Reset to example
        </Button>
      </Stack>

      <OrderOutcomeAlert error={error} outcome={outcome} />
    </Box>
  );
};

export default EdiPayloadPanel;
